import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { unwrapRef } from "@/utils/reactive";

import {
    OutOfSquareCode,
    OverdoorNoneCode,
    OverdoorTransomCode,
    SheetMaterialCode,
} from "@/app/constants";
import {
    Hardware,
    Overdoor,
    OverdoorMaterial,
    Partition,
    OutOfSquare,
    OverdoorAsset,
    Door,
} from "@/app/domain";
import { OverdoorService } from "@/app/services/overdoor.service";

import { usePartitionStore } from "./partition.store";
import { useDoorsStore } from "./doors.store";
import { OverdoorHardware } from "@/app/domain/entities/hardware";
import { useNewEstimateStore } from "./estimateStore";
import { isCGFSystem, isCGSSystem } from "@/app/predicates/systems.predicates";

export const useOverdoorStore = defineStore(
    "overdoor-store",
    () => {
        const partitionsStore = usePartitionStore();
        const doorsStore = useDoorsStore();
        const estimateStore = useNewEstimateStore();

        const activePartition = computed(
            () => partitionsStore.activePartitions,
        );

        const installationSystem = computed(
            () => estimateStore.estimate.installedSystemCode,
        );
        const activeDoor = computed(() => doorsStore.activeDoor);
        const activeOverdoor = ref<Overdoor | null>(null);
        const activeTransomHardwares = ref<OverdoorHardware[]>([]);
        const activeOverdoorMaterials = ref<OverdoorMaterial[]>([]);

        const activeOverdoorAssets = ref<OverdoorAsset | null>(null);
        const activeOverdoorOutOfSquare = ref<OutOfSquare | null>(null);

        const activeGlassOverdoor = computed(() =>
            activeOverdoorMaterials.value.at(0),
        );
        const activeGlassThinknessOverdoor = computed(() =>
            activeOverdoorMaterials.value.at(1),
        );

        const overdoorMaterialType = ref<string | null>("glass");

        const overdoorType = computed(
            () => activeOverdoor.value?.structureType,
        );

        const hasActiveHardwares = computed(
            () => activeTransomHardwares.value.length > 0,
        );
        const hasActiveOverdoor = computed(() => activeOverdoor.value !== null && activeOverdoor.value !== undefined);
        const hasActiveOverdoorMaterial = computed(
            () => activeOverdoorMaterials.value.length > 0,
        );

        const $reset = () => {
            activeOverdoor.value = null;
            overdoorMaterialType.value = null;
            activeTransomHardwares.value = [];
            activeOverdoorMaterials.value = [];
            activeOverdoorAssets.value = null;
            activeOverdoorOutOfSquare.value = null;
        };

        const $restoreOverdoors = () => {
            setActiveOverdoor(activePartition.value);
        };

        const setActiveOverdoor = (partition?: Partition) => {
            if (!partition) return;

            const overdoor = partition?.getActiveOverdoor();
            const overdoorMaterial = overdoor?.getMaterial();
            const overdoorOutOfSquare = overdoor?.getOutOfSquare();

            const overdoorGlass = overdoor?.getActiveGlass();
            const overdoorGlassThickness = overdoor?.getActiveGlassThinkness();

            activeOverdoor.value = overdoor;

            activeOverdoorMaterials.value = [
                overdoorGlass,
                overdoorGlassThickness,
            ];

            activeOverdoorOutOfSquare.value = overdoorOutOfSquare;
        };

        const addOverdoor = () => {
            if (!activePartition.value) {
                return;
            }

            const overdoor = new Overdoor({
                structureType: detectOverdoorType(),
                qty: 1,
            });

            activePartition.value.addComponent(overdoor);
            activeOverdoor.value = overdoor;
        };

        const addOverdoorMaterial = () => {
            if (!activeOverdoor.value || !activePartition.value) return;

            const materials = OverdoorService.createOverdoorMaterial(
                overdoorMaterialType.value || "glass",
                activePartition.value,
            );

            activeOverdoor.value.replaceMaterial(materials);
            activeOverdoorMaterials.value = materials;
        };

        const addTransomHardware = () => {
            if (
                !activeOverdoor.value ||
                !overdoorType.value ||
                !activeDoor.value
            ) {
                return;
            }

            if (activeOverdoor.value.isFiller()) {
                activeDoor.value.replaceHardwares([]);
                activeTransomHardwares.value = [];
                return;
            }

            if (
                !installationSystem.value ||
                (!isCGFSystem(installationSystem.value) &&
                    !isCGSSystem(installationSystem.value))
            ) {
                return;
            }

            const hardwares = OverdoorService.createTransomHardwares(
                activeDoor.value.structureType || "",
            );
            activeOverdoor.value.replaceHardwares(hardwares);
            activeTransomHardwares.value = hardwares;
        };

        const addOverdoorAsset = () => {
            if (!activeOverdoor.value) {
                return;
            }

            const asset = new OverdoorAsset({
                qty: 1,
            });

            activeOverdoor.value.addComponent(asset);
            activeOverdoorAssets.value = asset;
        };

        const addOverdoorOutOfSquare = () => {
            if (!activeOverdoor.value) {
                return;
            }

            const outOfSquare = new OutOfSquare({
                code: OutOfSquareCode,
                structureType: OverdoorNoneCode,
                qty: 0,
            });

            activeOverdoor.value.addComponent(outOfSquare);
            activeOverdoorOutOfSquare.value = outOfSquare;
        };

        const detectOverdoorType = () => {
            if (!activeDoor.value || !activePartition.value) {
                return OverdoorNoneCode;
            }

            if (activeDoor.value.isDoorNone()) {
                return OverdoorNoneCode;
            }

            if (canUseTransom(activePartition.value, activeDoor.value)) {
                return OverdoorTransomCode;
            }

            return OverdoorNoneCode;
        };

        const syncOverdoorGlassWithPartition = () => {
            if (
                !activePartition.value ||
                !activeOverdoor.value ||
                !activeOverdoorMaterials.value
            ) {
                return;
            }

            if (overdoorMaterialType.value === SheetMaterialCode) {
                return;
            }

            const glass = activePartition.value.getActiveGlass();
            const glassThickness =
                activePartition.value.getActiveGlassThinkness();

            const overdoorGlass = activeOverdoorMaterials.value[0];
            const overdoorGlassThickness = activeOverdoorMaterials.value[1];

            if (!overdoorGlass || !overdoorGlassThickness) return;

            overdoorGlass.structureType = glass?.structureType;
            overdoorGlassThickness.structureType =
                glassThickness?.structureType;
        };

        const canUseTransom = (partition: Partition, door: Door) => {
            if (!partition || !door) return false;

            if (door.isDoorNone() || partition.isDivider()) return false;

            const doorPartitionHeightDiff = partition.dimensions.height.compare(
                unwrapRef(door.dimensions.height),
            );

            return doorPartitionHeightDiff > 0;
        };

        return {
            addOverdoor,
            addOverdoorMaterial,
            addTransomHardware,
            addOverdoorAsset,
            addOverdoorOutOfSquare,
            detectOverdoorType,
            syncOverdoorGlassWithPartition,
            setActiveOverdoor,
            canUseTransom,
            $reset,
            $restoreOverdoors,
            activeOverdoorAssets,
            activeOverdoorOutOfSquare,
            activeOverdoor,
            activeTransomHardwares,
            activeOverdoorMaterials,
            overdoorMaterialType,
            hasActiveOverdoor,
            hasActiveOverdoorMaterial,
            hasActiveHardwares,
            activeGlassOverdoor,
            activeGlassThinknessOverdoor,
        };
    },
    { persist: true },
);
